import { useParams, useSearchParams } from "react-router";
import { Page } from "../../analytics/Page.tsx";
import type { ApiGameDetails, ApiPlayerDetails } from "../../api";
import { Redirect } from "../../components";
import { ErrorScreen, LoadingScreen } from "../../screens";
import { useGameDetailsQuery, usePlayersQuery } from "./hooks";
import { PowerupPlayersForm } from "./PowerupPlayersForm";

export const PowerupPlayersPage = () => {
  const { gameId = "" } = useParams<{ gameId: string }>();

  const playerIds = useSearchParams()[0].get("playerIds")?.split(",") || [];

  const gameDetailsQuery = useGameDetailsQuery(gameId);
  const playersQuery = usePlayersQuery();

  if (gameDetailsQuery.isPending || playersQuery.isPending) {
    return <LoadingScreen />;
  }

  if (gameDetailsQuery.isError || playersQuery.isError) {
    return <ErrorScreen error={gameDetailsQuery.error || playersQuery.error} />;
  }

  const gameDetails = gameDetailsQuery.data;
  const players = playersQuery.data;

  const selectedPlayers = playerIds.map((playerId: string) =>
    players.find((player) => player.id === playerId),
  );

  if (!arePlayersValid(gameDetails, selectedPlayers)) {
    return <Redirect to={`/games/${gameId}/pick-your-players`} replace />;
  }

  return (
    <Page name="PowerupPlayers">
      <PowerupPlayersForm
        gameId={gameId}
        players={selectedPlayers}
        maxTokens={gameDetails.tokensGiven}
        maxTokensPerPlayer={gameDetails.tokenSteps.length}
      />
    </Page>
  );
};

function arePlayersValid(
  game: ApiGameDetails,
  selectedPlayers: Array<ApiPlayerDetails | undefined>,
): selectedPlayers is ApiPlayerDetails[] {
  // Ensure that there are exactly 3 players selected
  if (selectedPlayers.length !== 3) {
    return false;
  }

  // Ensure that all players are matched up
  const players = selectedPlayers.filter(
    (it): it is ApiPlayerDetails => it != null,
  );

  if (players.length !== 3) {
    return false;
  }

  // Ensure that all player ids are unique
  const uniquePlayerIds = new Set(players.map((player) => player.id));

  if (uniquePlayerIds.size !== players.length) {
    return false;
  }

  // Ensure that all player ids are valid and belong to the game
  const validPlayerIds = game.gamePlayers.map((gp) => gp.playerId);

  return players.every((player) => validPlayerIds.includes(player.id));
}
